// =============================================
// FILE: src/components/LastGoodbyes.js
// =============================================
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { db, storage } from "../firebase";
import {
  doc,
  getDoc,
  onSnapshot,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { FiEye } from "react-icons/fi";
import { FaYoutube } from "react-icons/fa";
import { useAccess } from "../access/AccessContext";
import PageScaffold from "./ui/PageScaffold";
import VideoLightbox from "./ui/VideoLightbox";
import {
  openDownloadForAttachment,
  toStoragePath,
  downloadTextFile,
} from "../utils/managerActions";
import "../styles/layout.css";

// Helpers (no UI changes)
function normalizeStoragePath(maybe) {
  return toStoragePath(maybe);
}

export default function LastGoodbyes({ user }) {
  // Access context (read-only manager view support)
  const { readOnly, ownerUid: managedOwnerUid } =
    useAccess() || { readOnly: false, ownerUid: null };

  // Data/UI state
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const [editing, setEditing] = useState(false);
  const [type, setType] = useState("text"); // text | audio | video
  const [subject, setSubject] = useState("");
   const [comments, setComments] = useState("");
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);

  const [mediaUrl, setMediaUrl] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [resolvedUrl, setResolvedUrl] = useState("");
  const [resolvedThumb, setResolvedThumb] = useState("");

  // Help/Video
  const [showDrawer, setShowDrawer] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  // Read-only “View” modal
  const [viewOpen, setViewOpen] = useState(false);

  // Recording
  const videoRef = useRef(null);
  const fileInput = useRef(null);
  const chunksRef = useRef([]);
  const [recording, setRecording] = useState(false);
  const [mediaStream, setMediaStream] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);

  // ----- Load user message (unchanged functionality)
  useEffect(() => {
    if (!user?.uid) return;
    setLoading(true);
    const refDoc = doc(db, "lastGoodbyes", user.uid);
    const stop = onSnapshot(
      refDoc,
      async (snap) => {
        if (!snap.exists()) {
          setMessage(null);
          setLoading(false);
          return;
        }
        const data = snap.data();
        setMessage(data);
        if (!editing) {
          setType(data.type || "text");
          setSubject(data.subject || "");
          setComments(data.comments || "");
          setText(data.content || "");

        }
        const convPath = data.convertedPath || "";
        const thPath = data.thumbPath || "";
        try {
          if (convPath) {
            const u = await getDownloadURL(storageRef(storage, convPath));
            setResolvedUrl(u);
          } else if (data.mediaUrl) {
            setResolvedUrl(data.mediaUrl);
          } else {
            setResolvedUrl("");
          }
        } catch {
          setResolvedUrl(data.mediaUrl || "");
        }
        try {
          if (thPath) {
            const tu = await getDownloadURL(storageRef(storage, thPath));
            setResolvedThumb(tu);
          } else {
            setResolvedThumb("");
          }
        } catch {
          setResolvedThumb("");
        }
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => stop();
  }, [user?.uid, editing]);

  // Preview URL for selected file
  useEffect(() => {
    if (!file) {
      setPreviewUrl("");
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // ----- Recording handlers (unchanged functionality)
  async function startRecording() {
    chunksRef.current = [];
    const constraints =
      type === "audio" ? { audio: true, video: false } : { audio: true, video: true };
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setMediaStream(stream);
      const options =
        type === "audio"
          ? { mimeType: "audio/webm" }
          : { mimeType: "video/webm;codecs=vp9,opus" };
      const rec = new window.MediaRecorder(stream, options);
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType });
        const ext = type === "audio" ? "webm" : "webm";
        const f = new File([blob], `goodbye.${ext}`, { type: blob.type });
        setFile(f);
        setRecording(false);
        stream.getTracks().forEach((t) => t.stop());
        setMediaStream(null);
        chunksRef.current = [];
      };
      rec.start();
      setMediaRecorder(rec);
      setRecording(true);
    } catch {
      alert("Unable to start recording on this device. You can upload a file instead.");
    }
  }
  function stopRecording() { try { mediaRecorder?.stop(); } catch {} }

  useEffect(() => {
    if (videoRef.current && recording && mediaStream && type === "video") {
      videoRef.current.srcObject = mediaStream;
    }
    return () => { if (videoRef.current) videoRef.current.srcObject = null; };
  }, [recording, mediaStream, type]);

  // ----- Form handlers (unchanged functionality)
  function handleTypeSwitch(val) {
    setType(val);
    setFile(null);
    setPreviewUrl("");
    setText("");
    if (fileInput.current) fileInput.current.value = "";
  }
  function handleFile(e) {
    const f = e.target.files && e.target.files[0];
    if (f) {
      setFile(f);
      setText("");
    }
  }
  async function handleSave(e) {
    e.preventDefault();
    if (readOnly) return; // guard

    if (type === "text" && !text.trim()) return;
    if ((type === "audio" || type === "video") && !file && !resolvedUrl) return;

    let uploadUrl = resolvedUrl;
    if ((type === "audio" || type === "video") && file) {
      const ext = (file.name.split(".").pop() || "webm").toLowerCase();
      const fname = `${user.uid}_${Date.now()}.${ext}`;
      const sRef = storageRef(storage, `lastGoodbyes/${user.uid}/${fname}`);
      await uploadBytesResumable(sRef, file);
      uploadUrl = await getDownloadURL(sRef);
    }

    await setDoc(
      doc(db, "lastGoodbyes", user.uid),
      {
        uid: user.uid,
        subject,
        comments,
        type,
        content: type === "text" ? text : "",
        mediaUrl: type === "text" ? "" : uploadUrl,
        converted: false,
        convertedPath: null,
        thumbPath: null,
        updated: serverTimestamp(),
      },
      { merge: true }
    );

    setEditing(false);
    setFile(null);
    setPreviewUrl("");
  }
  async function handleDelete() {
    if (readOnly) return; // guard
    if (!window.confirm("Delete your Last Goodbye?")) return;
    const refDoc = doc(db, "lastGoodbyes", user.uid);
    try {
      const current = (await getDoc(refDoc)).data();
      const paths = [];
      if (current?.mediaUrl) {
        const enc = current.mediaUrl.split("/o/")[1]?.split("?")[0];
        if (enc) paths.push(decodeURIComponent(enc));
      }
      if (current?.convertedPath) paths.push(current.convertedPath);
      if (current?.thumbPath) paths.push(current.thumbPath);
      await Promise.all(
        paths.map(async (p) => { try { await deleteObject(storageRef(storage, p)); } catch {} })
      );
    } catch {}
    await deleteDoc(refDoc);
    setMessage(null);
    setEditing(true);
    setSubject("");
     setComments("");
    setText("");
    setType("text");
    setFile(null);
    setMediaUrl("");
    setPreviewUrl("");
    setResolvedUrl("");
    setResolvedThumb("");
  }
  function startEdit() {
    if (readOnly) return; // guard
    setEditing(true);
    setType(message?.type || "text");
    setSubject(message?.subject || "");
    setComments(message?.comments || "");
    setText(message?.content || "");
    setMediaUrl(message?.mediaUrl || "");
    setPreviewUrl("");
    if (fileInput.current) fileInput.current.value = "";
  }

  if (loading) return <div className="empty-state" style={{ padding: 32 }}>Loading...</div>;

  // Header help buttons (unchanged)
  const actionsDesktop = (
    <>
      <button className="btn dp-help read" onClick={() => setShowDrawer(true)} data-allow-readonly>
        <FiEye className="dp-help-ico" /> Read how it works
      </button>
      <button className="btn dp-help see" onClick={() => setShowVideo(true)} data-allow-readonly>
        <FaYoutube className="dp-help-ico" /> See how it works
      </button>
    </>
  );
  const actionsMobile = (
    <div className="dp-help-row">
      <button className="btn dp-help read" onClick={() => setShowDrawer(true)} data-allow-readonly>
        <FiEye className="dp-help-ico" /> Read how it works
      </button>
      <button className="btn dp-help see" onClick={() => setShowVideo(true)} data-allow-readonly>
        <FaYoutube className="dp-help-ico" /> See how it works
      </button>
    </div>
  );

  function ViewTile() {
    const t = message?.type || "text";
    const title = message?.subject?.trim() || "Last Goodbye";
    const commentsSnippet = (message?.comments || "").trim();
    const fallbackSnippet =
      t === "text" ? (message?.content || "").trim() : "";
    const snippetSource = commentsSnippet || fallbackSnippet;
    const snippet = snippetSource
      ? snippetSource.length > 160
        ? `${snippetSource.slice(0, 160)}…`
        : snippetSource
      : "";
    const thumbContent = (() => {
      if (t === "video") {
        if (resolvedThumb) {
          return (
            <img src={resolvedThumb} alt="Video thumbnail" />
          );
        }
        if (resolvedUrl) {
          return (
            <video src={resolvedUrl} preload="metadata" playsInline muted />
          );
        }
        return <div className="lg-feature-placeholder" aria-label="Video message">🎥</div>;
      }
      if (t === "audio") {
        return <div className="lg-feature-placeholder" aria-label="Audio message">🎙️</div>;
      }
      return <div className="lg-feature-placeholder" aria-label="Text message">📝</div>;
    })();

    const onPrimary = readOnly ? () => setViewOpen(true) : startEdit;
    const label = readOnly ? "View" : "Edit";

    return (
      <article className="card lg-feature-card">
        <div className="lg-feature-thumb">{thumbContent}</div>
        <div className="lg-feature-body">
          <h3 className="lg-feature-title">{title}</h3>
          {snippet && <p className="lg-feature-snippet">{snippet}</p>}
          <div className="lg-feature-actions">
            <button
              className="btn btn-main btn-edit"
              onClick={onPrimary}
              {...(readOnly ? { "data-allow-readonly": "1" } : {})}
            >
              {label}
            </button>
            {!readOnly && (
              <button className="btn btn-danger" onClick={handleDelete}>
                Delete
              </button>
            )}
          </div>
        </div>
      </article>
    );
  }

  const showEditor = !readOnly && (editing || !message);
  const streamPreview = recording && type === "video";
  const playableUrl = previewUrl || resolvedUrl || mediaUrl;

  function renderEditorForm() {
    if (!showEditor) return null;
    return (
      <form className="lg-card" onSubmit={handleSave}>
        <div className="lg-grid">
          <div className="lg-field">
            <label className="lg-label">Subject (optional)</label>
            <input
              type="text"
              className="lg-input"
              placeholder="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          <div className="lg-field">
            <label className="lg-label">Type</label>
            <div className="lg-segment" role="tablist" aria-label="Message type">
              <button
                type="button"
                role="tab"
                aria-selected={type === "text"}
                className={"lg-seg-btn" + (type === "text" ? " is-active" : "")}
                onClick={() => handleTypeSwitch("text")}
              >
                Text
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={type === "audio"}
                className={"lg-seg-btn" + (type === "audio" ? " is-active" : "")}
                onClick={() => handleTypeSwitch("audio")}
              >
                Audio
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={type === "video"}
                className={"lg-seg-btn" + (type === "video" ? " is-active" : "")}
                onClick={() => handleTypeSwitch("video")}
              >
                Video
              </button>
            </div>
          </div>
        </div>

        <div className="lg-field">
          <label className="lg-label">Comments (optional)</label>
          <textarea
            className="lg-input lg-textarea lg-textarea-compact"
            rows={3}
            placeholder="Add context or notes just for yourself"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
          />
        </div>

        {type === "text" && (
          <div className="lg-field">
            <label className="lg-label">Your message</label>
            <textarea
              className="lg-input lg-textarea"
              rows={8}
              placeholder="Write your final message…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              required
            />
          </div>
        )}

        {(type === "audio" || type === "video") && (
          <div className="lg-field">
            <label className="lg-label">{type === "audio" ? "Record or upload audio" : "Record or upload video"}</label>

            {!file && !previewUrl && !playableUrl && (
              <div className="lg-row">
                <button type="button" className="btn btn-main" onClick={startRecording}>
                  {type === "audio" ? "🎤 Start recording" : "🎥 Start recording"}
                </button>
                <label className="lg-file">
                  <input
                    ref={fileInput}
                    type="file"
                    accept={type === "audio" ? "audio/*" : "video/*"}
                    onChange={handleFile}
                  />
                  <span className="lg-file-btn">Choose file</span>
                  <span className="lg-file-hint">{type === "audio" ? "MP3/WAV/WEBM" : "MP4/WEBM/MOV"}</span>
                </label>
              </div>
            )}

            {streamPreview && (
              <video ref={videoRef} autoPlay muted playsInline className="lg-media live" />
            )}
            {recording && type === "audio" && (
              <div className="lg-recording-dot">● Recording…</div>
            )}

            {(file || playableUrl) && (
              <div className="lg-preview">
                {type === "audio" ? (
                  <audio className="lg-media" src={file ? previewUrl : playableUrl} controls />
                ) : (
                  <>
                    {!message?.converted && !file && playableUrl && (
                      <div className="lg-hint">
                        Processing uploaded video… you’ll be able to play once it’s ready.
                      </div>
                    )}
                    <video
                      className="lg-media"
                      src={file ? previewUrl : playableUrl}
                      controls
                      playsInline
                      preload="metadata"
                    />
                  </>
                )}
              </div>
            )}

            {recording && (
              <div className="lg-row">
                <button type="button" className="btn btn-cancel" onClick={stopRecording}>
                  Stop recording
                </button>
              </div>
            )}
          </div>
        )}

        <div className="lg-actions">
          <button className="btn btn-main" type="submit">
            Save
          </button>
          {message && (
            <button className="btn btn-cancel" type="button" onClick={() => setEditing(false)}>
              Cancel
            </button>
          )}
          {message && (
            <button className="btn btn-danger" type="button" onClick={handleDelete}>
              Delete
            </button>
          )}
        </div>
      </form>
    );
  }

  return (
    <>
      <PageScaffold
        title="Last Goodbyes"
        onAdd={!readOnly && !message ? () => setEditing(true) : undefined}
        heroSrc="/lastwish-account-last-goodbye.jpg"
        heroAlt="Last Goodbye"
        actionsDesktop={actionsDesktop}
        actionsMobile={actionsMobile}
      >
        {!showEditor && message ? <ViewTile /> : renderEditorForm()}
      </PageScaffold>

      {/* Read-only VIEW modal (unchanged functionality) */}
      {readOnly && viewOpen && message && (
        <div className="modal-bg" onClick={() => setViewOpen(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 680 }}>
            <h3>{message.subject?.trim() || "Last Goodbye"}</h3>
            <div className="modal-body">
              {message.type === "text" ? (
                <>
                  <label className="label">Message</label>
                  <div className="card" style={{ padding: 12, whiteSpace: "pre-wrap" }}>{message.content || "-"}</div>
                </>
              ) : message.type === "audio" ? (
                <>
                  <label className="label">Audio</label>
                  <audio controls src={resolvedUrl || ""} style={{ width: "100%" }} />
                </>
              ) : (
                <>
                  <label className="label">Video</label>
                  <video controls src={resolvedUrl || ""} style={{ width: "100%" }} playsInline preload="metadata" />
                </>
              )}

{(message.comments || "").trim() && (
                <>
                  <label className="label" style={{ marginTop: 16 }}>Comments</label>
                  <div className="card" style={{ padding: 12, whiteSpace: "pre-wrap" }}>{message.comments}</div>
                </>
              )}

              <div className="form-actions" style={{ marginTop: 10 }}>
                <button
                  className="btn btn-main"
                  type="button"
                  onClick={async () => {
                    const owner = managedOwnerUid || user?.uid;
                    if (!owner) return;

                    const t = message?.type || "text";
                    if (t === "text") {
                      const title = (message?.subject?.trim() || "Last_Goodbye").replace(/[^\w.-]+/g, "_");
                      downloadTextFile(`${title}.txt`, message?.content || "");
                      return;
                    }

                    const base =
                      message?.convertedPath ||
                      normalizeStoragePath(message?.mediaUrl) ||
                      message?.mediaUrl ||
                      null;

                    const suggested =
                      (message?.subject?.trim()
                        ? `${message.subject.trim().replace(/[^\w.-]+/g, "_")}.${t === "audio" ? "mp3" : "mp4"}`
                        : (t === "audio" ? "audio.mp3" : "video.mp4"));

                    const ok = await openDownloadForAttachment({
                      ownerUid: owner,
                      pathOrUrl: base,
                      suggestedName: suggested,
                    });

                    if (!ok) alert("Could not generate a download link.");
                  }}
                  data-allow-readonly
                >
                  Download
                </button>
                <button className="btn btn-main" type="button" onClick={() => window.print()} data-allow-readonly>
                  Print
                </button>
                <a
                  className="btn btn-main"
                  href={`mailto:?subject=${encodeURIComponent(message.subject || "Last Goodbye")}&body=${encodeURIComponent(
                    message.type === "text"
                      ? message.content || "-"
                      : "Please see the attached link."
                  )}`}
                  data-allow-readonly
                >
                  Forward
                </a>
                <button className="btn btn-cancel" type="button" onClick={() => setViewOpen(false)} data-allow-readonly>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VideoLightbox (unchanged) */}
      <VideoLightbox
        open={showVideo}
        onClose={() => setShowVideo(false)}
        url="https://youtu.be/nqHKbCqVf58?si=18hfla4nCji57HSj"
        title="How the Last Goodbyes page works"
        startAt={0}
        captions={false}
        startMuted={false}
      />

      {/* Drawer (unchanged) */}
      {showDrawer && (
        <div className="dp-drawer-bg" onClick={() => setShowDrawer(false)}>
          <aside className="dp-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="dp-drawer-head">
              <div className="dp-drawer-title">How the Last Goodbyes page works</div>
              <div className="dp-drawer-tools">
                <button className="btn btn-main" onClick={() => window.print()} type="button" data-allow-readonly>
                  Print
                </button>
                <button className="btn btn-cancel" onClick={() => setShowDrawer(false)} type="button" data-allow-readonly>
                  Close
                </button>
              </div>
            </div>
            <div className="dp-drawer-body">
              <p><b>One message only.</b> Create a single final message as Text, Audio, or Video.</p>
              <ol>
                <li>Pick the type, then write or record your message.</li>
                <li>Upload a file if you prefer.</li>
                <li>Save. If it’s audio/video we’ll convert it for best playback on all devices.</li>
              </ol>
            </div>
          </aside>
        </div>
      )}

      <style>{`
        /* ===== Card + grid ===== */
        .lg-card { border:1px solid #e5e7eb; border-radius:14px; background:#fff; padding:16px; }
        .lg-grid { display:grid; grid-template-columns:1fr; gap:14px; }
        @media (min-width: 720px) { .lg-grid { grid-template-columns:1fr 1fr; } }

        /* ===== Fields ===== */
        .lg-field { display:flex; flex-direction:column; gap:8px; margin-bottom:12px; }
        .lg-label { color:#374151; font-size:13px; font-weight:600; }
        .lg-input { width:100%; min-height:42px; border:1px solid #d6d6e7; border-radius:10px; padding:10px 12px; }
        .lg-textarea { min-height:160px; resize:vertical; line-height:1.5; }
         .lg-textarea-compact { min-height:90px; }

        /* ===== Segmented control ===== */
        .lg-segment { display:flex; gap:8px; background:#f3f4f6; padding:6px; border-radius:999px; width:fit-content; }
        .lg-seg-btn { height:34px; padding:0 14px; border:none; border-radius:999px; background:transparent; cursor:pointer; font-weight:700; color:#374151; }
        .lg-seg-btn.is-active { background:#650202; color:#fff; }

        /* ===== File input ===== */
        .lg-row { display:flex; gap:10px; align-items:center; flex-wrap:wrap; }
        .lg-file { position:relative; display:inline-flex; align-items:center; gap:10px; }
        .lg-file input[type="file"] { position:absolute; inset:0; opacity:0; cursor:pointer; }
        .lg-file-btn { display:inline-flex; align-items:center; justify-content:center; height:40px; padding:0 14px; border-radius:10px; background:#003353; color:#fff; font-weight:700; }
        .lg-file-hint { color:#6b7280; font-size:12px; }

        /* ===== Media preview ===== */
        .lg-preview { margin-top:8px; }
        .lg-media { width:100%; border-radius:12px; border:1px solid #e5e7eb; background:#000; }
        .lg-media.live { background:#111; }
        .lg-recording-dot { color:#b91c1c; font-weight:700; margin-top:6px; }

        /* ===== Actions ===== */
        .lg-actions { display:flex; gap:10px; flex-wrap:wrap; margin-top:8px; }

       /* ===== Featured tile ===== */
        .lg-feature-card { padding:0; overflow:hidden; border-radius:18px; display:flex; flex-direction:column; border:1px solid #e5e7eb; }
        .lg-feature-thumb { width:100%; aspect-ratio:16/9; background:#f4f5f9; display:flex; align-items:center; justify-content:center; }
        .lg-feature-thumb img,
        .lg-feature-thumb video { width:100%; height:100%; object-fit:cover; display:block; }
        .lg-feature-thumb video { background:#000; }
        .lg-feature-placeholder { font-size:2.5rem; }
        .lg-feature-body { padding:18px; display:flex; flex-direction:column; gap:12px; }
        .lg-feature-title { margin:0; font-size:1.25rem; color:#111827; }
        .lg-feature-snippet { margin:0; color:#374151; line-height:1.55; }
        .lg-feature-actions { display:grid; grid-template-columns:repeat(auto-fit, minmax(140px, 1fr)); gap:10px; margin-top:4px; }
        .lg-feature-actions .btn { width:100%; justify-content:center; }

        :root { --topbar-h: 56px; --orange:#f15822; --blue:#8cade1; }
        .dp-help-row { display:flex; gap:10px; flex-wrap:wrap; }
        .dp-help { border-radius: 8px; padding: 10px 14px; display: inline-flex; align-items: center; gap: 10px; font-weight: 700; color: #fff; border: none; }
        .dp-help.read { background: #650202; color: #fff; }
        .dp-help.see  { background: #003353; color: #fff; }
        .dp-help-ico { font-size: 1.05rem; }
        .dp-intro { margin: 4px 0 10px; color: #2a0516; }

        /* Drawer (kept) */
        .dp-drawer-bg { position:fixed; inset:0; background:rgba(0,0,0,0.3); z-index:10000; display:flex; justify-content:flex-end; }
        .dp-drawer { position: fixed; top: var(--topbar-h, 56px); right: 0; height: calc(100vh - var(--topbar-h, 56px)); width:min(520px, 95vw); background: #fff; box-shadow: -8px 0 24px rgba(0,0,0,0.15); border-top-left-radius: 12px; border-bottom-left-radius: 12px; display: flex; flex-direction: column; overflow: hidden; }
        .dp-drawer-head { display:flex; align-items:center; justify-content:space-between; gap:10px; padding:14px 16px; border-bottom:1px solid #f0f0f0; background:#fafafa; }
        .dp-drawer-title { font-weight:700; color:#2a0516; }
        .dp-drawer-tools { display:flex; gap:8px; }
        .dp-drawer-body { padding:16px; overflow:auto; }
      `}</style>
    </>
  );
}
